import {
  FIXED_HEADER_OFFSET,
  FIXED_MOBILE_HEADER_OFFSET_PX,
} from '../../components/layouts/headerLayout';
import { getFilterBandBackground } from '../../components/layouts/SubHeader/subHeaderBannerStyles';

const LIGHT_CANVAS_GRADIENT = 'linear-gradient(180deg,#F7F2FF,#FBF7F1 50%,#fff)';
const DARK_CANVAS_GRADIENT = 'linear-gradient(180deg,#1A1530,#141225 55%,#121212)';
const MOBILE_HEADER_OFFSET = `${FIXED_MOBILE_HEADER_OFFSET_PX}px`;
const RESPONSIVE_HEADER_OFFSET = { xs: MOBILE_HEADER_OFFSET, md: FIXED_HEADER_OFFSET };
const responsiveViewportHeight = (unit) => ({
  xs: `calc(100${unit} - ${MOBILE_HEADER_OFFSET})`,
  md: `calc(100${unit} - ${FIXED_HEADER_OFFSET})`,
});

export const CHAT_WRAPPER_SX = {
  width: '100%',
  mt: RESPONSIVE_HEADER_OFFSET,
  height: responsiveViewportHeight('vh'),
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  bgcolor: 'background.default',
  color: 'text.primary',
  '@supports (height: 100dvh)': {
    height: responsiveViewportHeight('dvh'),
  },
};

export const CHAT_BANNER_SX = {
  flexShrink: 0,
};

export const CHAT_BODY_SX = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: 'flex',
  overflow: 'hidden',
};

export const CHAT_CONTENT_SX = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

/** V2/V3 content pane — page surface, separated from the sidebar by a gray stroke. */
export const CHAT_CONTENT_MODERN_SX = {
  ...CHAT_CONTENT_SX,
  position: 'relative',
  bgcolor: 'background.default',
};

export const CHAT_CONTENT_PANE_SX = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Search-page banner sits in the chat content column (sidebar → right edge),
 * not across the full window.
 */
export const CHAT_CONTENT_BANNER_LAYER_SX = {
  flexShrink: 0,
  width: '100%',
};

/**
 * Same Search-page filter-band fade, continuing the banner wash into the chat
 * canvas (sidebar → right edge) then dissolving into the page surface.
 */
export const CHAT_BANNER_EXTENSION_HEIGHT_PX = 128;

export const CHAT_BANNER_EXTENSION_SX = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: { xs: 220, md: CHAT_BANNER_EXTENSION_HEIGHT_PX },
  pointerEvents: 'none',
  zIndex: 0,
  background: getFilterBandBackground,
};

export const CHAT_CONTENT_MAIN_SX = {
  position: 'relative',
  zIndex: 1,
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

export const CHAT_MODERN_CANVAS_SX = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'transparent',
};

export const CHAT_CANVAS_SX = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: (theme) =>
    theme.palette.mode === 'dark' ? DARK_CANVAS_GRADIENT : LIGHT_CANVAS_GRADIENT,
  color: 'text.primary',
};

export const CHAT_MAIN_SX = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const CHAT_MOBILE_HISTORY_BAR_SX = {
  minHeight: '56px',
  pt: 'env(safe-area-inset-top)',
  px: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#181818' : 'background.paper'),
  flexShrink: 0,
};

export const CHAT_SIDEBAR_DRAWER_PAPER_SX = {
  width: 'min(88vw, 300px)',
  maxWidth: '100vw',
  height: '100vh',
  overflow: 'hidden',
  bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#181818' : 'background.paper'),
  '@supports (height: 100dvh)': {
    height: '100dvh',
  },
};

export const CHAT_INDEX_CANVAS_SX = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  bgcolor: 'transparent',
};

export const CHAT_INDEX_LANE_SX = {
  width: '100%',
  maxWidth: '720px',
  minWidth: 0,
  mx: 'auto',
  px: { xs: 2, sm: 3 },
  py: { xs: 2, md: 2.5 },
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
  boxSizing: 'border-box',
};

export const CHAT_LIBRARY_LANE_SX = {
  width: '100%',
  maxWidth: { xs: '100%', md: 1080 },
  minWidth: 0,
  mx: 'auto',
  px: { xs: 2.5, sm: 4, md: 5 },
  py: { xs: 2.5, md: 3.5 },
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2, md: 2.5 },
  boxSizing: 'border-box',
};

export const THREAD_WRAPPER_STYLE = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

export const EMPTY_STATE_STYLE = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'clamp(24px, 7vh, 64px) 24px',
  boxSizing: 'border-box',
};

export const EMPTY_CONTENT_STYLE = {
  width: '100%',
  maxWidth: '760px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
};

export const EMPTY_HEADING_SX = {
  color: 'text.primary',
  fontSize: { xs: '28px', sm: '34px' },
  lineHeight: { xs: '36px', sm: '42px' },
  fontWeight: 800,
  letterSpacing: 0,
  textAlign: 'center',
  margin: 0,
};
